import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { commonImages } from '@images/common-images';
import { ApiService } from '@services/api/api.service';
import { ProjectService } from '@services/api/project.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { Utils } from '@services/helpers/utils';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

/**
 * Fixed display order for the "Agreement Status" dropdown — the tag list is otherwise
 * alphabetical (see agreement_type's own sort below, unaffected), but that put "Executed
 * Agreement Sent" ahead of "Signed Agreement Returned", out of step with the actual agreement
 * lifecycle (sent → signed copy returned → formally executed). A status tag not in this list
 * (e.g. one added later in office settings) still appears — sorted alphabetically among itself —
 * appended after these three, so it's never silently dropped.
 */
const AGREEMENT_STATUS_ORDER = ['Agreement Sent', 'Signed Agreement Returned', 'Executed Agreement Sent'];

@Component({
  selector: 'app-project-agreement',
  templateUrl: './project-agreement.component.html',
  styleUrls: ['./project-agreement.component.scss'],
})
export class ProjectAgreementComponent {
  @Output() is_agreement = new EventEmitter<boolean>();
  @Input() agree: any;

  public agreement_Form: FormGroup;
  public projectId: any;
  agencyList: any[] = [];
  agentList: any[] = [];
  internalAgentList: any[] = [];
  agreement_type: any[] = [];
  agreement_status: any[] = [];
  common_images = commonImages;

  constructor(
    private Api: ApiService,
    private toaster: ToastrService,
    public projectService: ProjectService,
    private localStorageService: LocalStorageService,
    public lmService: LeadManagementService,
    private fb: FormBuilder,
    private redux: ReduxService,
    private remmiConfirm: RemmiConfirmService
  ) {
    this.agreement_Form = this.fb.group({
      project_id: null,
      agency: null,
      agent: null,
      internal_agent: null,
      agreement_type: null,
      agreement_status: null,
      agreement_sent: null,
      agreement_exe_date: null,
      comments: null,
      flat_fee: null,
      commission_comments: null,
      commission: null,
      bonus: null,
      agreement_cancelled: null,
    });
  }

  /**
   * Lifecycle hook that runs on component initialization.
   * - Fetches the project list and short contacts from the lmService.
   * - Filters the short company list to retrieve agencies.
   * - Retrieves office users and project-related tags.
   * - Retrieves the project ID from local storage and updates the form if necessary.
   * - Fetches the project agreement details.
   */
  ngOnInit(): void {
    this.lmService.GetProjectList();
    // this.lmService.getShortContacts();
    // this.agencyList = this.lmService.shortCompany?.filter(el => el?.company_type?.toLowerCase() == 'agency');
    this.getAgencyList();
    this.getOfficeUsers();
    this.getTags();

    let pid = Crypto.decrypt(this.localStorageService.GetItem('pid'));
    if (pid !== null && pid !== undefined) {
      this.projectId = pid;
      if (!this.agree?.project) {
        this.agreement_Form.patchValue({
          project_id: pid,
        });
      }
    }

    this.getAgreement();
  }

  getAgencyList() {
    let office_id = this.Api.getOfficeId();
    let user_id = this.Api.getUserId();
    let url = `/contacts/list?office_id=${office_id}&uid=${user_id}&type=All&page=1&per_page=990&user_type=Contact&company_type=${JSON.stringify(['Agency'])}`;
    this.Api.get(url).subscribe({
      next: (response: any) => {
        this.agencyList = response?.data;
      },
    });
  }

  getAgentList(eve: any) {
    this.Api.get(`/contacts/company-agents/${eve?._id}`).subscribe({
      next: (response: any) => {
        this.agentList = response?.data;
      },
    });
  }

  /**
   * Fetches the details of the project agreement if an agreement ID exists.
   * - Calls the API to retrieve agreement details.
   * - Updates the agreement form with the fetched data.
   * - Converts `agreement_sent` and `agreement_exe_date` fields to Date objects if they exist.
   */
  getAgreement() {
    if (this.agree?._id) {
      this.Api.get(`/project-agreement-detail/${this.agree?._id}`).subscribe({
        next: (response: any) => {
          this.agreement_Form.patchValue(response?.data);
          this.agreement_Form.patchValue({
            agreement_sent: response?.data?.agreement_sent ? new Date(response?.data?.agreement_sent) : null,
            agreement_exe_date: response?.data?.agreement_exe_date ? new Date(response?.data?.agreement_exe_date) : null,
          });
          this.getAgentList({ _id: response?.data?.agency });
        },
      });
    }
  }

  /**
   * Deletes the current project agreement.
   * - Calls the API to delete the agreement.
   * - Displays a success notification on successful deletion.
   * - Resets the agreement-related variables and form.
   * - Emits an event indicating the agreement has been deleted.
   */
  deleteAgreement() {
    this.remmiConfirm.delete({ title: 'Delete Agreement?', message: 'This agreement will be permanently deleted.' }).subscribe(isConfirmed => {
      if (!isConfirmed) return;
      this.Api.delete(`/project-agreement/${this.agree?._id}`).subscribe({
        next: () => {
          this.toaster.success('Deleted successfully');
          this.agree = null;
          this.agreement_Form.reset();
          this.is_agreement.emit(true);
        },
      });
    });
  }

  /**
   * Cancels the current project agreement.
   * - Resets the agreement-related variables and form.
   * - Emits an event indicating the agreement has been canceled.
   */
  cancelAgreement() {
    this.agree = null;
    this.agreement_Form.reset();
    this.is_agreement.emit(true);
  }

  inserted_id = null;
  singleClick = false;
  /**
   * Saves or updates the project agreement based on existing conditions.
   * - Sets `singleClick` to `true` to prevent multiple submissions.
   * - Retrieves form data and appends `office_id` and `user_id` from the API.
   * - If an `inserted_id` exists, updates the agreement using a PUT request.
   * - If `inserted_id` is not present but `agree._id` exists, updates the agreement using a PUT request.
   * - If neither `inserted_id` nor `agree._id` exist, creates a new agreement using a POST request.
   * - If `param` is 'create_close', resets the form and agreement state after successful API response.
   *
   * @param param - Determines whether to reset and close the form after submission.
   */
  saveAgreement(param) {
    this.singleClick = true;
    let form = this.agreement_Form.value;
    form['office_id'] = this.Api.getOfficeId();
    form['user_id'] = this.Api.getUserId();
    form['agreement_sent'] = form['agreement_sent'] ? moment(form['agreement_sent']).format('YYYY-MM-DD') : null;
    form['agreement_exe_date'] = form['agreement_exe_date'] ? moment(form['agreement_exe_date']).format('YYYY-MM-DD') : null;
    if (this.inserted_id) {
      this.Api.put('/project-agreement/' + this.inserted_id, form).subscribe({
        next: () => {
          this.toaster.success('Agreement updated successfully');
          if (param == 'create_close') {
            this.agree = null;
            this.inserted_id = null;
            this.agreement_Form.reset();
            this.is_agreement.emit(true);
          }
          this.singleClick = false;
        },
      });
    } else {
      if (this.agree?._id) {
        this.Api.put('/project-agreement/' + this.agree?._id, form).subscribe({
          next: () => {
            this.toaster.success('Agreement updated successfully');
            if (param == 'create_close') {
              this.agree = null;
              this.agreement_Form.reset();
              this.is_agreement.emit(true);
            }
            this.singleClick = false;
          },
        });
      } else {
        this.Api.post('/project-agreement', form).subscribe({
          next: (response: any) => {
            this.toaster.success('Agreement added successfully');
            this.inserted_id = response?.insertedId;
            if (param == 'create_close') {
              this.agreement_Form.reset();
              this.is_agreement.emit(true);
            }
            this.singleClick = false;
          },
        });
      }
    }
  }

  tagsData: any[] = [];
  /**
   * Retrieves tags related to the project using Redux.
   * - Fetches tags based on the office ID.
   * - Filters and sorts tags based on `agreement-type` and `agreement-status`.
   */
  getTags() {
    this.redux.getTags(this.Api.getOfficeId(), false, 'project').subscribe({
      next: response => {
        this.tagsData = [...response?.data];
        this.tagsData[0]?.types.forEach(tag => {
          if (tag?.slug == 'agreement-type') {
            this.agreement_type = [...tag?.data].sort((a, b) => a?.tag_name.localeCompare(b?.tag_name));
          }
          if (tag?.slug == 'agreement-status') {
            this.agreement_status = [...tag?.data].sort((a, b) => {
              const aIndex = AGREEMENT_STATUS_ORDER.indexOf(a?.tag_name);
              const bIndex = AGREEMENT_STATUS_ORDER.indexOf(b?.tag_name);
              if (aIndex === -1 && bIndex === -1) {
                return a?.tag_name.localeCompare(b?.tag_name);
              }
              if (aIndex === -1) return 1;
              if (bIndex === -1) return -1;
              return aIndex - bIndex;
            });
          }
        });
      },
    });
  }

  public role_id = '';
  public SortBy = '';
  public Users: any[];

  /**
   * Retrieves office users with optional filters.
   * - Fetches users based on pagination, role, query, sort order, and office ID.
   * - Filters out users with the role "Master Admin".
   *
   * @param page - Page number for pagination (default: null).
   * @param per_page - Number of users per page (default: 'all').
   * @param role - Role filter for users (default: this.role_id).
   * @param query - Search query string (default: '').
   * @param SortBy - Sorting criteria (default: this.SortBy).
   * @param uid - User ID (default: fetched from API).
   * @param officeid - Office ID (default: fetched from API).
   * @param include - Additional related data to include (default: 'groups,offices').
   */
  getOfficeUsers(
    page = null,
    per_page = 'all',
    role = this.role_id,
    query = '',
    SortBy = this.SortBy,
    uid = this.Api.getUserId(),
    officeid = this.Api.getOfficeId(),
    include = 'groups,offices'
  ) {
    SortBy = this.SortBy;
    if (uid && officeid) {
      this.Api.get('/users/office-staff-list', {
        page: page,
        per_page: per_page,
        role: role,
        search: query,
        uid: uid,
        officeid: officeid,
        sort: SortBy,
        show: 'staff',
        include,
      }).subscribe({
        next: data => {
          this.Users = data?.data?.filter(u => u.role !== 'Master Admin');
        },
      });
    }
  }

  selectDate(controller: string) {
    if (controller == 'agreement_sent') {
      let date_value = this.agreement_Form.controls['agreement_sent'].value;
      let val = Utils.getDateStringFormat(date_value);
      this.agreement_Form.controls['agreement_sent'].setValue(new Date(val));
    } else {
      let date_value = this.agreement_Form.controls['agreement_exe_date'].value;
      let val = Utils.getDateStringFormat(date_value);
      this.agreement_Form.controls['agreement_exe_date'].setValue(new Date(val));
    }
  }
}
